import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtHora, hoyISO, fechaISOhaceDias, isoADatetimeLocal, datetimeLocalAIso } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, ArrowSquareIn, Pencil, FunnelSimple, MapPin, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canSupervise } from "@/contexts/AuthContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import CheckinMap from "@/components/CheckinMap";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_jornadas_seen";

function tourSteps(user) {
  const steps = [
    { target: "[data-tour='jornadas-filtros']", title: "Filtra el periodo", content: "Elige el rango de fechas y, si tu rol lo permite, un empleado específico para acotar la bitácora." },
  ];
  if (canSupervise(user)) {
    steps.push({ target: "[data-tour='jornadas-manual']", title: "Captura manual autorizada", content: "Cuando falle el checador biométrico puedes registrar entrada/salida manualmente. El motivo es obligatorio y queda en auditoría." });
  }
  steps.push({ target: "[data-tour='jornadas-tabla']", title: "Bitácora de jornadas", content: "Cada renglón tiene valor probatorio conforme la LFT: horas ordinarias, extra, retardos y el método de registro." });
  return steps;
}

const STATUS_COLOR = {
  valido: "bg-emerald-100 text-emerald-800",
  pendiente: "bg-amber-100 text-amber-800",
  corregido: "bg-blue-100 text-blue-800",
  justificado: "bg-violet-100 text-violet-800",
  rechazado: "bg-red-100 text-red-800",
};

// Fotos de evidencia del registro: entrada y salida por separado. `evidencia_url`
// es el campo legado (foto única) de registros anteriores a la separación.
function fotosEvidencia(r) {
  const fotos = [];
  if (r.evidencia_entrada_url) fotos.push({ label: "Entrada", url: r.evidencia_entrada_url });
  if (r.evidencia_salida_url) fotos.push({ label: "Salida", url: r.evidencia_salida_url });
  if (!fotos.length && r.evidencia_url) fotos.push({ label: "Evidencia", url: r.evidencia_url });
  return fotos;
}

export default function Jornadas() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [centros, setCentros] = useState([]);
  const [mapRow, setMapRow] = useState(null);
  const [fotoRow, setFotoRow] = useState(null);
  const [filters, setFilters] = useState({
    fecha_desde: fechaISOhaceDias(7),
    fecha_hasta: hoyISO(),
    empleado_id: "todos",
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ empleado_id: "", fecha: "", hora_entrada: "", hora_salida: "", motivo: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const params = new URLSearchParams();
    if (filters.fecha_desde) params.set("fecha_desde", filters.fecha_desde);
    if (filters.fecha_hasta) params.set("fecha_hasta", filters.fecha_hasta);
    if (filters.empleado_id && filters.empleado_id !== "todos") params.set("empleado_id", filters.empleado_id);
    const [j, e, c] = await Promise.all([
      api.get("/jornadas?" + params.toString()),
      user.role === "empleado" ? Promise.resolve({ data: [] }) : api.get("/empleados"),
      user.role === "empleado" ? Promise.resolve({ data: [] }) : api.get("/centros"),
    ]);
    setItems(j.data);
    setEmpleados(e.data);
    setCentros(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const empName = (id) => empleados.find((e) => e.id === id)?.nombre || id;
  const centroFor = (id) => centros.find((c) => c.id === id) || null;
  // Zona horaria del centro del empleado seleccionado en la captura manual, para
  // interpretar los campos datetime-local como hora local de esa ubicación.
  const tzManual = centroFor(empleados.find((e) => e.id === manualForm.empleado_id)?.centro_id)?.zona_horaria;

  const submitManual = async () => {
    setBusy(true);
    try {
      await api.post("/jornadas/manual", manualForm);
      toast.success("Registro manual creado");
      setManualOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/jornadas/${editForm.id}`, editForm);
      toast.success("Registro corregido");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="jornadas-page">
      <PageHeader
        kicker="Registro electrónico"
        title="Jornadas"
        subtitle="Bitácora completa de entradas, salidas, descansos y horas trabajadas con valor probatorio."
        actions={
          <div className="flex gap-2 items-center">
            {canSupervise(user) && (
              <Button onClick={() => { setManualForm({ empleado_id: empleados[0]?.id || "", fecha: hoyISO(), hora_entrada: "", hora_salida: "", motivo: "" }); setManualOpen(true); }} className="bg-brand hover:bg-brand-hover" data-testid="manual-new-btn" data-tour="jornadas-manual">
                <ArrowSquareIn size={16} weight="bold" className="mr-2" /> Captura manual
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 flex gap-3 flex-wrap items-end" data-testid="jornadas-filters" data-tour="jornadas-filtros">
        <FunnelSimple size={20} className="text-slate-500 mb-2" />
        <div>
          <Label className="label-uppercase text-[10px]">Desde</Label>
          <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })} className="h-9 w-40" />
        </div>
        <div>
          <Label className="label-uppercase text-[10px]">Hasta</Label>
          <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })} className="h-9 w-40" />
        </div>
        {user.role !== "empleado" && (
          <div>
            <Label className="label-uppercase text-[10px]">Empleado</Label>
            <Select value={filters.empleado_id} onValueChange={(v) => setFilters({ ...filters, empleado_id: v })}>
              <SelectTrigger className="h-9 w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={load} className="bg-brand hover:bg-brand-hover h-9" data-testid="jornadas-apply-filter">Aplicar filtros</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="jornadas-tabla">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Empleado</th>
              <th>Entrada</th>
              <th>Salida</th>
              <th>H. Ord</th>
              <th>H. Extra</th>
              <th>Retardo</th>
              <th>Método</th>
              <th>Estatus</th>
              <th><span className="inline-flex items-center gap-1"><Camera size={13} weight="bold" /> Foto</span></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={12} className="text-center text-slate-500 py-8">Sin registros para el periodo</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`jor-row-${r.id}`}>
                <td className="font-mono text-xs">{r.fecha}</td>
                <td className="font-semibold">{empName(r.empleado_id)}</td>
                <td className="font-mono">{fmtHora(r.hora_entrada, r.zona_horaria)}</td>
                <td className="font-mono">{fmtHora(r.hora_salida, r.zona_horaria)}</td>
                <td className="font-mono font-bold">{r.horas_ordinarias}</td>
                <td className="font-mono font-bold text-emerald-700">{r.horas_extra > 0 ? `+${r.horas_extra}` : "0"}</td>
                <td className={r.minutos_retardo > 0 ? "text-amber-700 font-bold" : "text-slate-500"}>{r.minutos_retardo}m</td>
                <td><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{r.metodo}</span></td>
                <td><span className={`text-xs px-2 py-1 rounded ${STATUS_COLOR[r.estatus]}`}>{r.estatus}</span></td>
                <td>
                  {fotosEvidencia(r).length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setFotoRow(r)}
                      className="flex gap-1"
                      title="Ver fotos de evidencia"
                      data-testid={`jor-foto-${r.id}`}
                    >
                      {fotosEvidencia(r).map((f) => (
                        <img
                          key={f.label}
                          src={f.url}
                          alt={`Evidencia ${f.label} ${empName(r.empleado_id)} ${r.fecha}`}
                          className="w-9 h-9 rounded-md object-cover border border-slate-200 hover:ring-2 hover:ring-slate-300"
                          loading="lazy"
                        />
                      ))}
                    </button>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td>
                  {r.geo_lat != null && (
                    <Button variant="ghost" size="sm" onClick={() => setMapRow(r)} data-testid={`jor-map-${r.id}`}>
                      <MapPin size={14} />
                    </Button>
                  )}
                </td>
                <td>
                  {canSupervise(user) && (
                    <Button variant="ghost" size="sm" onClick={() => { setEditForm(r); setEditOpen(true); }} data-testid={`jor-edit-${r.id}`}>
                      <Pencil size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!mapRow} onOpenChange={(o) => !o && setMapRow(null)}>
        <DialogContent data-testid="jor-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Ubicación del check-in · {empName(mapRow?.empleado_id)} · {mapRow?.fecha}
            </DialogTitle>
          </DialogHeader>
          {mapRow && (
            <CheckinMap
              eventos={mapRow.geo_eventos}
              lat={mapRow.geo_lat}
              lng={mapRow.geo_lng}
              distanciaMetros={mapRow.geo_distancia_metros}
              dentroGeocerca={mapRow.geo_dentro_geocerca}
              centro={centroFor(mapRow.centro_id)}
              height={360}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!fotoRow} onOpenChange={(o) => !o && setFotoRow(null)}>
        <DialogContent data-testid="jor-foto-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Fotos de evidencia · {empName(fotoRow?.empleado_id)} · {fotoRow?.fecha}
            </DialogTitle>
          </DialogHeader>
          {fotoRow && (
            <div className="space-y-4">
              {fotosEvidencia(fotoRow).map((f) => (
                <div key={f.label} className="space-y-1">
                  <div className="label-uppercase text-[10px] text-slate-500">{f.label}</div>
                  <img
                    src={f.url}
                    alt={`Evidencia ${f.label} ${empName(fotoRow.empleado_id)} ${fotoRow.fecha}`}
                    className="w-full rounded-md border border-slate-200"
                  />
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-slate-700 underline"
                  >
                    Abrir en tamaño completo
                  </a>
                </div>
              ))}
              <p className="text-xs text-slate-500">
                Fotos capturadas durante el check-in. Se conservan como evidencia de presencia.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Captura Manual Autorizada</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase">Empleado</Label>
              <Select value={manualForm.empleado_id} onValueChange={(v) => setManualForm({ ...manualForm, empleado_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="label-uppercase">Fecha</Label><Input type="date" value={manualForm.fecha} onChange={(e) => setManualForm({ ...manualForm, fecha: e.target.value })} /></div>
              <div className="space-y-1"><Label className="label-uppercase">Entrada</Label><Input type="datetime-local" value={isoADatetimeLocal(manualForm.hora_entrada, tzManual)} onChange={(e) => setManualForm({ ...manualForm, hora_entrada: e.target.value ? datetimeLocalAIso(e.target.value, tzManual) : "" })} /></div>
              <div className="space-y-1"><Label className="label-uppercase">Salida</Label><Input type="datetime-local" value={isoADatetimeLocal(manualForm.hora_salida, tzManual)} onChange={(e) => setManualForm({ ...manualForm, hora_salida: e.target.value ? datetimeLocalAIso(e.target.value, tzManual) : "" })} /></div>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Motivo (obligatorio)</Label>
              <Input value={manualForm.motivo} onChange={(e) => setManualForm({ ...manualForm, motivo: e.target.value })} placeholder="Ej: Falla de checador biométrico" data-testid="manual-motivo-input" />
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded">
              <strong>Aviso:</strong> Este registro queda en bitácora con tu usuario, fecha y motivo para fines de auditoría.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button onClick={submitManual} disabled={busy || !manualForm.motivo} className="bg-brand hover:bg-brand-hover" data-testid="manual-save-btn">
              {busy ? "Guardando…" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="jor-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Corregir registro</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="label-uppercase">Entrada</Label><Input type="datetime-local" value={isoADatetimeLocal(editForm.hora_entrada, editForm.zona_horaria)} onChange={(e) => setEditForm({ ...editForm, hora_entrada: e.target.value ? datetimeLocalAIso(e.target.value, editForm.zona_horaria) : null })} /></div>
              <div className="space-y-1"><Label className="label-uppercase">Salida</Label><Input type="datetime-local" value={isoADatetimeLocal(editForm.hora_salida, editForm.zona_horaria)} onChange={(e) => setEditForm({ ...editForm, hora_salida: e.target.value ? datetimeLocalAIso(e.target.value, editForm.zona_horaria) : null })} /></div>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Observaciones</Label>
              <Input value={editForm.observaciones || ""} onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Motivo de la corrección (obligatorio)</Label>
              <Input value={editForm.motivo || ""} onChange={(e) => setEditForm({ ...editForm, motivo: e.target.value })} placeholder="Ej: Corrige omisión de salida reportada por el empleado" data-testid="jor-edit-motivo-input" />
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded">
              <strong>Aviso:</strong> Esta corrección queda en bitácora con tu usuario, fecha, motivo y el valor anterior del registro.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={submitEdit} disabled={busy || !editForm.motivo} className="bg-brand hover:bg-brand-hover" data-testid="jor-edit-save-btn">
              {busy ? "Guardando…" : "Guardar corrección"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
